/**
 * Application-level constants.
 * Tariff logic lives in tariff.ts — do not mix.
 */

// ─── Data Source ──────────────────────────────────────────────────────────────
// Per-meter sheet IDs / gids live in the access-code registry (auth/AuthContext).
// Data is fetched from each sheet's single flat tab via the public GViz CSV
// endpoint (see api/sheetsClient.ts) — no global sheet env vars are needed.

/** Auto-refresh interval (ms). Default 5 minutes. */
export const REFRESH_MS = Number(import.meta.env.VITE_REFRESH_MS ?? 300_000);

/** Site display name shown in the header. */
export const SITE_NAME =
  import.meta.env.VITE_SITE_NAME ?? 'Vireon Pilot';

// ─── Sensor Quality ───────────────────────────────────────────────────────────

/** L-N voltage below this is a sensor dropout — never a valid reading. */
export const VOLTAGE_DROPOUT_V = 100;

/** Percentile used for outlier/spike rejection. */
export const OUTLIER_PERCENTILE = 99.5;

/** Maximum credible inter-sample gap for energy integration (hours). */
export const MAX_INTEGRATION_GAP_H = 1;

// ─── Anomaly Detection Defaults ───────────────────────────────────────────────
// These are configurable from the Alerts page; these are the factory defaults.

export const DEFAULT_THRESHOLDS = {
  neutralCurrentA:      5.0,   // A — fire-risk indicator
  voltageUnbalancePct:  2.0,   // %
  currentUnbalancePct:  10.0,  // %
  pfMin:                0.92,  // triggers low-PF anomaly
  loadPctMax:           95.0,  // % of rated capacity
  frequencyDeviationHz: 0.5,   // Hz deviation from 50 Hz
} as const;

export type AnomalyThresholds = typeof DEFAULT_THRESHOLDS;

// ─── Risk Levels ──────────────────────────────────────────────────────────────

export type RiskLevel = 'NORMAL' | 'WARNING' | 'HIGH' | 'CRITICAL';

export const RISK_META: Record<RiskLevel, { color: string; bg: string; label: string }> = {
  NORMAL:   { color: '#10B981', bg: '#ECFDF5', label: 'Normal'   },
  WARNING:  { color: '#F59E0B', bg: '#FFFBEB', label: 'Warning'  },
  HIGH:     { color: '#F97316', bg: '#FFF7ED', label: 'High'     },
  CRITICAL: { color: '#EF4444', bg: '#FEF2F2', label: 'Critical' },
};
